import { ref } from 'vue'
import { storeToRefs } from 'pinia'
import { useToast } from 'vue-toastification'
import { useAuthStore } from '~/stores/auth/AuthStore'
import AuthApi from '~/api/AuthApi'
import router from '~/boot/plugins/router'
import { emitLogoutBeacon } from '~/services/auth/logoutBeacon'
import type { LogoutOptions, LogoutService } from '~/ts/interfaces/services/logout.interface'

type ToastID = string | number

const SESSION_EXPIRED_TOAST_ID: ToastID = 'session-expired'
const CROSS_TAB_LOGOUT_MESSAGE = 'You have been signed out in another tab.'
const SESSION_EXPIRED_MESSAGE = 'Your session has expired. Please sign in again.'
const LOGOUT_RELEASE_DELAY_MS = 1000

const showLogoutDialog = ref<boolean>(false)

/**
 * Module-level state: a single AbortController is shared by every authenticated
 * request so a single `abort()` cancels all in-flight calls when logout begins.
 * `isBusyFlag` prevents re-entry if multiple 401s race each other.
 */
let abortController = new AbortController()
let isBusyFlag = false

const resetAbortController = (): void => {
  abortController = new AbortController()
}

export const getLogoutSignal = (): AbortSignal => abortController.signal

export const isLoggingOut = (): boolean => isBusyFlag

/**
 * Atomic logout. Aborts pending requests, notifies the server, resets local
 * auth state, surfaces a single dedup'd toast when the logout was NOT user-
 * initiated, then navigates to the login page.
 */
export const performLogout = async (opts: LogoutOptions = {}): Promise<void> => {
  if (isBusyFlag) return
  isBusyFlag = true

  const { reason = 'user', skipServer = false } = opts
  const { resetAuthUser } = useAuthStore()

  try {
    abortController.abort('logout')
    resetAbortController()

    if (!skipServer) {
      await AuthApi.logout().catch(() => {
        // Best-effort: if the /logout call fails (network, 401 from a stale
        // token, …) we still want to clear local state and redirect.
      })
    }
  } finally {
    resetAuthUser()

    if (reason === 'cross_tab') {
      const toast = useToast()
      toast.warning(CROSS_TAB_LOGOUT_MESSAGE, { id: SESSION_EXPIRED_TOAST_ID })
    } else if (reason !== 'user') {
      const toast = useToast()
      toast.warning(SESSION_EXPIRED_MESSAGE, { id: SESSION_EXPIRED_TOAST_ID })
    }

    showLogoutDialog.value = false
    await router.push('/auth/login').catch(() => {})

    // Notify other same-origin tabs AFTER local teardown completes. Skipped for
    // the cross-tab receive path itself so a received beacon never re-emits and
    // loops back to the tabs that triggered it.
    if (reason !== 'cross_tab') {
      emitLogoutBeacon()
    }

    setTimeout(() => {
      isBusyFlag = false
    }, LOGOUT_RELEASE_DELAY_MS)
  }
}

export const handleSessionExpired = (): Promise<void> =>
  performLogout({ reason: 'session_expired' })

export function useLogoutService(): LogoutService {
  const { authUser } = storeToRefs(useAuthStore())
  const { resetAuthUser } = useAuthStore()

  const triggerLogout = () => {
    showLogoutDialog.value = false
    showLogoutDialog.value = true
  }

  return {
    showLogoutDialog,
    authUser,
    resetAuthUser,
    triggerLogout,
    performLogout,
    handleSessionExpired,
    isLoggingOut,
    getLogoutSignal,
  }
}

/**
 * Test-only helper: reset module-level state so dedup / busy flags do not
 * leak between test cases.
 */
export const __resetLogoutServiceForTests = (): void => {
  abortController = new AbortController()
  isBusyFlag = false
  showLogoutDialog.value = false
}
